// The "Eerder onderzoek" tab of a project (ProjectEarlierResearch.vue): row
// labels and links, and the chat prompts its quick actions prefill. The rows
// come from the getEarlierResearch callable
// (functions/modules/earlier-research). Pure, so it is unit-tested in
// tests/unit/earlierResearch.test.js.

const text = (v) => (typeof v === "string" && v.trim() ? v.trim() : "");

export const KIND_LABELS = {
  sub_study: "deelstudie",
  loose_files: "losse bestanden",
  year_bucket: "jaarmap",
  range_bucket: "map met projecten",
  staffing: "inhuur of uren",
};

export const SOURCE_LABELS = {
  app: "Alice",
  archive: "Archief",
  both: "Alice + archief",
};

export const LINK_LABELS = {
  report: "Rapport",
  test_script: "Testscript",
  proposal: "Offerte",
  folder: "Map",
};

// "2026", "~2014" when the year is derived from the job number or the text,
// "—" when unknown.
export function yearLabel(row) {
  if (!row || !Number.isInteger(row.year)) return "—";
  return row.year_inferred ? `~${row.year}` : String(row.year);
}

export function yearTitle(row) {
  if (!row || !Number.isInteger(row.year)) return "Jaar onbekend";
  return row.year_inferred
    ? "Jaar geschat uit het projectnummer of de tekst"
    : "";
}

// Alice projects open in Alice, on their insights (as the chat's sources do).
export function rowRoute(row) {
  const id = row && text(row.app_id);
  return id ? `/project/${id}/insights` : null;
}

// The archive file a row's title opens: its report, else test script,
// proposal or Drive folder (the server orders them).
export function primaryLink(row) {
  const links = row && Array.isArray(row.links) ? row.links : [];
  return links.find((l) => l && /^https:\/\//.test(l.url)) || null;
}

// Hover text when the methods are the archive's keyword tags (no project card
// yet): they are a guess, not what the study did.
export function methodsHint(row) {
  return row && row.methods_tag && Array.isArray(row.methods)
    ? "Methoden afgeleid uit trefwoorden in het archief; dit project heeft nog geen projectkaart."
    : "";
}

// "Vorige ronde · 1/2": the direction first, the number when the catalog
// knows it.
export function roundLabel(row, direction) {
  const label = direction === "next" ? "Volgende ronde" : "Vorige ronde";
  if (row && Number.isInteger(row.round) && Number.isInteger(row.of)) {
    return `${label} · ${row.round}/${row.of}`;
  }
  return label;
}

// "17 projecten · 2014–2026 · 2 zonder jaar"
export function historySummary(history) {
  if (!history || !Number.isInteger(history.total)) return "";
  const n = history.total;
  const parts = [`${n} ${n === 1 ? "project" : "projecten"}`];
  const y = history.years;
  if (y && Number.isInteger(y.from) && Number.isInteger(y.to)) {
    parts.push(y.from === y.to ? String(y.from) : `${y.from}–${y.to}`);
  }
  if (history.undated) parts.push(`${history.undated} zonder jaar`);
  return parts.join(" · ");
}

// How a row is named in a prompt: “Title” (Client, 2023).
function cite(row) {
  const tail = [text(row.client), Number.isInteger(row.year) ? row.year : ""]
    .filter(Boolean)
    .join(", ");
  return `“${text(row.title) || row.id}”${tail ? ` (${tail})` : ""}`;
}

const previousHint = (previous) => {
  const first = Array.isArray(previous) ? previous[0] : null;
  return first
    ? ` Volgens de projectcatalogus is dat ${cite(first)}; controleer dat.`
    : "";
};

// "Vergelijk met vorige ronde": prefilled in Project Chat, never sent.
export function comparePreviousRoundPrompt({ previous } = {}) {
  const known = Array.isArray(previous) && previous.length > 0;
  return [
    "Vergelijk dit project met de vorige ronde.",
    "",
    `1. Zoek de vorige ronde met get_previous_round.${
      known
        ? previousHint(previous)
        : " Vind je geen vorige ronde, zeg dat dan en noem de drie eerdere projecten van deze klant die het dichtst bij dit project liggen."
    }`,
    "2. Haal de bevindingen van die ronde op met get_project_insights.",
    "3. Leg ze naast de inzichten en sessies van dit project. Geef per bevinding uit de vorige ronde aan of die is bevestigd, opgelost of niet opnieuw getest, en zet nieuwe problemen apart.",
    "",
    "Houd eerder onderzoek gescheiden van het bewijs uit dit project. Noem bij elke eerdere bevinding klant · project · jaar, en zeg welke bronnen je niet kon lezen.",
  ].join("\n");
}

// "Bouwstenen testscript": the create-testscript skill with its search step.
export function testscriptPrompt({ previous } = {}) {
  return [
    "Gebruik de skill create-testscript. Zoek eerst de bouwstenen voor het testscript van dit project:",
    `- De vorige ronde, met script en bevindingen (get_previous_round).${previousHint(
      previous
    )}`,
    "- Eén vergelijkbaar script (find_similar_scripts).",
    "- Vragen uit de vragenbank voor de pre-test en de evaluatie (question_bank).",
    "",
    "Gebruik de onderzoeksvragen, de doelgroep en het testmateriaal uit de projectgegevens. Label elk hergebruikt item met klant · project · jaar, en markeer taken uit de vorige ronde met “nog relevant?”.",
    "",
    "Mijn notities voor het script: ",
  ].join("\n");
}

// "Vraag Alice" on one row: what this project can learn from that one.
export function askAboutPrompt(row) {
  if (!row) return "";
  const appId = text(row.app_id);
  const archiveId = text(row.archive_id);
  const read = [];
  if (appId) {
    read.push(
      `haal de bevindingen op met get_project_insights (project_id: "${appId}")`
    );
  }
  if (archiveId) {
    read.push(
      `lees de documenten met get_archive_project (archive_project_id: "${archiveId}")`
    );
  }
  return [
    `Wat kan dit project leren van ${cite(row)}?`,
    "",
    `${
      read.length ? read.join(" en ") : "zoek het project op met find_projects"
    }. Geef wat voor dit project relevant is, met bron (klant · project · jaar en pagina of link), en houd het gescheiden van het bewijs uit dit project.`.replace(
      /^./,
      (c) => c.toUpperCase()
    ),
  ].join("\n");
}

// The full client history, for when the tab shows only the newest projects.
export function fullHistoryPrompt(client) {
  const name = text(client);
  if (!name) return "";
  return `Geef alle projecten die we voor ${name} hebben gedaan (get_client), nieuwste eerst, met jaar, methode en of het in Alice of in het archief staat. Zet projecten zonder jaar apart.`;
}

// Notes of both calls, each once.
export function mergeNotes(...responses) {
  const out = [];
  for (const res of responses) {
    for (const n of (res && Array.isArray(res.notes) && res.notes) || []) {
      if (text(n) && !out.includes(n)) out.push(n);
    }
  }
  return out;
}

// ---- Linking to Drive -------------------------------------------------------

// The Drive folder id in what a colleague pasted: a folder link
// ("https://drive.google.com/drive/folders/…", also with "/u/0/" or
// "?usp=sharing") or a bare folder id. Null for anything else, a file link
// included (the server checks the folder itself).
export function driveFolderInput(value) {
  const t = text(value);
  if (/^[A-Za-z0-9_-]{10,100}$/.test(t)) return t;
  const m = t.match(
    /^https:\/\/drive\.google\.com\/drive\/(?:u\/\d+\/)?folders\/([A-Za-z0-9_-]{10,100})(?:[/?#].*)?$/
  );
  return m ? m[1] : null;
}

export function driveFolderUrl(id) {
  return `https://drive.google.com/drive/folders/${encodeURIComponent(id)}`;
}

// What the tab says about when search follows a decision.
export const SEARCH_FOLLOWS =
  "Zoeken in Alice en de MCP-tools volgt na de nachtelijke bijwerking van de projectcatalogus.";

// The project fields linkDriveFolder writes (functions/modules/archive-sync/
// link.js), for the project in the store: a later Save writes the project
// doc from there, and must not put an old value back. A link made here is
// made by hand (drive_linked_via null), also when it replaces one the job
// number made.
export function driveLinkFields(result, uid, now = Date.now()) {
  const linked = Boolean(result && result.folder && result.folder.id);
  return {
    drive_folder_id: linked ? result.folder.id : null,
    archive_project_id: linked ? result.archive_project_id || null : null,
    drive_linked_at: linked ? now : null,
    drive_linked_by: linked ? uid || null : null,
    drive_linked_via: null,
  };
}

// Why the linked Drive study is this project's: linked by hand on this tab,
// or from the job number on the Details page (setProjectJobNumber).
export function driveLinkReason(drive) {
  const code = drive && drive.via === "job_number" ? text(drive.code) : "";
  return code
    ? `gekoppeld via jobnummer ${code}`
    : "handmatig gekoppeld in Alice";
}

// linkDriveFolder's and rejectStudyLink's errors, in words a colleague can
// act on. The server's messages are English; known ones get their own Dutch.
const LINK_MESSAGES = [
  [
    /outside the shared drives/i,
    "Deze map staat niet in de klantendrives die het archief synchroniseert.",
  ],
  [
    /does not exist or the archive sync cannot see it/i,
    "Die map bestaat niet, of het archief kan er niet bij.",
  ],
  [/not a folder/i, "Deze link is geen map. Plak de link van de projectmap."],
  [
    /project folder inside a client folder/i,
    "Kies een projectmap binnen een klantmap, niet de klantmap zelf.",
  ],
  [
    /not in the archive map yet/i,
    "Deze map staat nog niet in het archief. Probeer het over een uur opnieuw.",
  ],
  [/in the trash/i, "Deze map staat in de prullenbak."],
  [
    /already linked to another Alice project/i,
    "Deze map is al aan een ander Alice-project gekoppeld.",
  ],
  [
    /archive sync is not switched on/i,
    "Koppelen kan nog niet: de archiefsync staat uit.",
  ],
  [/Drive folder link or folder id/i, "Plak de link van een map op Drive."],
  [/Archive study not found/i, "Deze Drive-studie bestaat niet meer."],
];

export function linkErrorMessage(err) {
  const code = String((err && err.code) || "").replace(/^functions\//, "");
  const message = String((err && err.message) || "");
  for (const [re, nl] of LINK_MESSAGES) if (re.test(message)) return nl;
  if (code === "permission-denied" || code === "unauthenticated") {
    return "Alleen het Valsplat-team kan studies koppelen.";
  }
  if (code === "not-found") return "Dit project is niet gevonden.";
  return "Dat is niet gelukt. Probeer het opnieuw.";
}

// A callable error, in words a colleague can act on.
export function errorMessage(err) {
  const code = String((err && err.code) || "").replace(/^functions\//, "");
  if (code === "permission-denied") {
    return "Eerder onderzoek is alleen beschikbaar voor het Valsplat-team.";
  }
  if (code === "not-found") {
    return "Dit project is niet gevonden.";
  }
  return "Eerder onderzoek kon niet worden geladen. Probeer het opnieuw.";
}
